import { apiGet, apiPost, apiPut, API_BASE, getMultipartAuthHeaders } from '@/utils/api';
import type {
  JuryFiltersResponse,
  GradeTrackingFilters,
  GradeTrackingReport,
  JuryPvData,
  StudentDecisionUpdate,
  ProgressionSummary,
} from '@/types/jury-deliberation.types';

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getDateSuffix(): string {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
}

// ==================== FILTERS ====================

export async function getJuryFilters(): Promise<JuryFiltersResponse> {
  return apiGet<JuryFiltersResponse>('/jury-deliberation/filters');
}

// ==================== GRADE TRACKING (SUIVI DES NOTES) ====================

export async function getGradeTrackingReport(filters: GradeTrackingFilters): Promise<GradeTrackingReport> {
  const params = new URLSearchParams();
  if (filters.academicYearId) params.append('academicYearId', filters.academicYearId);
  if (filters.filiereIds?.length) params.append('filiereIds', filters.filiereIds.join(','));
  if (filters.niveaux?.length) params.append('niveaux', filters.niveaux.join(','));
  if (filters.campusIds?.length) params.append('campusIds', filters.campusIds.join(','));

  return apiGet<GradeTrackingReport>(`/jury-deliberation/grade-tracking?${params.toString()}`);
}

// ==================== JURY PV ====================

export async function generatePv(data: {
  academicYearId: string;
  filiereId: string;
  campusId: string;
  niveau: number;
}): Promise<JuryPvData> {
  return apiPost<JuryPvData>('/jury-deliberation/generate', data);
}

export async function getPvByFilters(
  academicYearId: string,
  filiereId: string,
  niveau: number,
  campusId: string,
): Promise<JuryPvData | null> {
  const params = new URLSearchParams({
    academicYearId,
    filiereId,
    niveau: String(niveau),
    campusId,
  });

  try {
    const response = await fetch(`${API_BASE}/jury-deliberation/by-filters?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getMultipartAuthHeaders(),
      },
    });

    if (response.status === 404) return null;
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Erreur lors du chargement du PV');
    }
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text);
  } catch (error) {
    if (error instanceof Error && error.message.includes('404')) {
      return null;
    }
    throw error;
  }
}

export async function getPvById(id: string): Promise<JuryPvData> {
  return apiGet<JuryPvData>(`/jury-deliberation/${id}`);
}

// ==================== DECISIONS ====================

export async function updateDecisions(data: {
  juryDeliberationId: string;
  decisions: StudentDecisionUpdate[];
}): Promise<JuryPvData> {
  return apiPut<JuryPvData>('/jury-deliberation/decisions', data);
}

export async function regenerateGrades(juryDeliberationId: string): Promise<JuryPvData> {
  return apiPost<JuryPvData>('/jury-deliberation/regenerate-grades', { juryDeliberationId });
}

/**
 * Re-apply the decisions already recorded on a PV (promotion, diplôme,
 * redoublement, archivage) without changing any of them. Idempotent.
 */
export async function applyProgression(juryId: string): Promise<ProgressionSummary> {
  return apiPost<ProgressionSummary>(`/jury-deliberation/apply-progression/${juryId}`, {});
}

/** Same, for every PV of an academic year at once. */
export async function applyProgressionForYear(
  academicYearId: string,
): Promise<ProgressionSummary & { pvCount: number; failed: number }> {
  return apiPost<ProgressionSummary & { pvCount: number; failed: number }>(
    `/jury-deliberation/apply-progression-year/${academicYearId}`,
    {},
  );
}

export async function signPv(juryId: string): Promise<JuryPvData> {
  return apiPost<JuryPvData>(`/jury-deliberation/${juryId}/sign`, {});
}

// ==================== EXPORTS ====================

export async function exportPvPdf(juryId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/jury-deliberation/export/pv-pdf/${juryId}`, {
    method: 'GET',
    headers: getMultipartAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Erreur lors de l'export PDF");
  }
  const blob = await response.blob();
  downloadBlob(blob, `pv-deliberation-${getDateSuffix()}.pdf`);
}

export async function exportPvExcel(juryId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/jury-deliberation/export/pv-excel/${juryId}`, {
    method: 'GET',
    headers: getMultipartAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Erreur lors de l'export Excel");
  }
  const blob = await response.blob();
  downloadBlob(blob, `pv-deliberation-${getDateSuffix()}.xlsx`);
}

export async function exportDiplomesExcel(academicYearId: string): Promise<void> {
  const params = new URLSearchParams({ academicYearId });
  const response = await fetch(`${API_BASE}/jury-deliberation/export/diplomes-excel?${params.toString()}`, {
    method: 'GET',
    headers: getMultipartAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Erreur lors de l'export de la liste des diplômés");
  }
  const blob = await response.blob();
  downloadBlob(blob, `liste-diplomes-${getDateSuffix()}.xlsx`);
}
